import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import type { ToolRef } from '@crowdex/plugin-contracts';
import { cn } from '@crowdex/ui/lib/utils';
import { ArrowUpRight } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { messageArtifacts } from '@/components/desktop-message-part/artifact-card';
import { ItemIcon } from '@/components/item-icon';
import { ArtifactPreview, CompactPreview } from '@/features/apps/artifact-preview';
import { artifactCard, ConversationAppCard } from '@/features/apps/conversation-cards';
import { type RefApp, useRefTargets } from '@/features/apps/ref-targets';
import { artifactOfRef, messageRefs, type RefPlace } from '@/lib/conversations/tool-refs';

/** Previews a message shows before "See all · N" (brand: "Trois aperçus par message"). */
export const MESSAGE_PREVIEWS = 3;

/** A state's tone: what is done in green, what failed in red, what waits in amber. */
export function statusTone(status: string): string {
  const value = status.toLowerCase();
  if (['merged', 'succeeded', 'done', 'completed', 'success', 'passed'].includes(value))
    return 'text-success';
  if (['failed', 'failure', 'error', 'cancelled', 'canceled'].includes(value))
    return 'text-destructive';
  if (['waiting', 'queued', 'pending', 'busy', 'draft'].includes(value)) return 'text-attention';
  return 'text-muted-foreground';
}

/** A ref's state as a word ("Merged", "Open"). */
export function statusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/[_-]+/g, ' ');
}

/** The 140 px body: the artifact's own preview, else what the ref brings (an image, rows, text). */
function PreviewBody({
  target,
  artifact,
}: {
  target: ToolRef;
  artifact: ArtifactListingEntry | undefined;
}) {
  if (artifact !== undefined) return <ArtifactPreview artifact={artifact} className="h-[140px]" />;
  return <CompactPreview className="h-[140px]" preview={target.preview} />;
}

/**
 * One thing a message touched, as the brand's preview (`brand/crow-extensions.html`, "Dans une
 * conversation"): the message's width up to 480 px, a 44 px header (its app's icon, its title,
 * app · detail, its state), and for an app's or an editor's a 140 px live body. A click opens it
 * as its row in the Summary does: docked beside the conversation, or at its service ↗.
 */
export function RefPreviewCard({
  target: ref,
  app,
  place,
  artifact,
  shown = false,
  onOpen,
}: {
  target: ToolRef;
  app: RefApp;
  place: RefPlace;
  artifact?: ArtifactListingEntry | undefined;
  /** It is what the side panel shows now: the card says so (its accent border). */
  shown?: boolean;
  onOpen: () => void;
}) {
  const service = place === 'service';
  const meta = [artifact?.pluginTitle || app.title, ref.detail].filter(Boolean).join(' · ');
  return (
    <section
      aria-current={shown ? 'true' : undefined}
      aria-label={ref.title}
      className={cn(
        'relative w-full max-w-[480px] overflow-hidden rounded-card border bg-card transition-colors',
        shown ? 'border-primary' : 'border-hairline hover:border-hairline-strong',
      )}
      data-ref-place={place}
      data-ref-preview={ref.id}
      data-ref-shown={shown ? '' : undefined}
    >
      <div className="flex h-11 items-center gap-2.5 px-3">
        <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">
          <ItemIcon className="size-4" icon={app.icon} src={app.iconSrc} />
        </span>
        <div className="min-w-0 flex-1 leading-tight">
          <p className="truncate font-semibold text-foreground text-sm">{ref.title}</p>
          <p className="type-meta truncate text-muted-foreground">{meta}</p>
        </div>
        {ref.status !== undefined && (
          <span className={cn('type-meta shrink-0', statusTone(ref.status))}>
            {statusLabel(ref.status)}
          </span>
        )}
        {service && ref.url !== undefined && (
          <ArrowUpRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        )}
      </div>
      {!service && (
        <div className="border-hairline border-t">
          <PreviewBody artifact={artifact} target={ref} />
        </div>
      )}
      <button
        aria-label={service ? `${ref.title} · ${app.title} ↗` : `Open ${ref.title}`}
        className="absolute inset-0 rounded-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={onOpen}
        title={service ? ref.url : undefined}
        type="button"
      />
    </section>
  );
}

/** "See all · N" under a section that shows a first batch; "Show less" folds it back. */
export function SeeAllButton({
  count,
  expanded,
  onToggle,
  className,
}: {
  count: number;
  expanded: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      aria-expanded={expanded}
      className={cn(
        'type-meta shrink-0 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      onClick={onToggle}
      type="button"
    >
      {expanded ? 'Show less' : `See all · ${count}`}
    </button>
  );
}

/**
 * What a message made or touched, previewed under it: the artifacts its turn wrote (their
 * cards), then what its tools' results name (an app's item, an editor's artifact, a service's
 * result). Three, then "See all · N". Nothing else around them.
 */
export function MessagePreviews({
  message,
  artifacts,
  onOpenArtifact,
}: {
  message: CrowdexUIMessage;
  artifacts: readonly ArtifactListingEntry[];
  onOpenArtifact: (artifact: ArtifactListingEntry) => void;
}) {
  const targets = useRefTargets(artifacts);
  const [expanded, setExpanded] = useState(false);
  const written = useMemo(() => messageArtifacts(message, artifacts), [message, artifacts]);
  const refs = useMemo(() => messageRefs(message), [message]);
  const shownArtifact =
    targets.shown !== null && 'artifact' in targets.shown ? targets.shown.artifact : null;
  const previews: { key: string; node: ReactNode }[] = written.map((artifact) => ({
    key: `artifact:${artifact.id}`,
    node: (
      // The one the side panel shows says so, as a ref's card does.
      <div
        className={cn(
          'w-full max-w-[480px] rounded-card',
          shownArtifact === artifact.id && 'ring-1 ring-primary',
        )}
        data-artifact-shown={shownArtifact === artifact.id ? '' : undefined}
      >
        <ConversationAppCard
          artifacts={artifacts}
          card={artifactCard(artifact)}
          conversation={artifact.conversation ?? ''}
          onOpenArtifact={onOpenArtifact}
        />
      </div>
    ),
  }));
  const shown = new Set(written.map((artifact) => artifact.id));
  for (const ref of refs) {
    const place = targets.placeOf(ref);
    // A task is listed in the Summary; the transcript already shows its step.
    if (place === 'work') continue;
    const artifact = artifactOfRef(ref, artifacts);
    if (artifact !== undefined && shown.has(artifact.id)) continue;
    if (artifact !== undefined) shown.add(artifact.id);
    previews.push({
      key: `ref:${ref.kind}:${ref.app}:${ref.type ?? ''}:${ref.id}`,
      node: (
        <RefPreviewCard
          app={targets.appOf(ref.app)}
          artifact={artifact}
          onOpen={() => targets.open(ref)}
          place={place}
          shown={targets.isShown(ref, artifact?.id)}
          target={ref}
        />
      ),
    });
  }
  if (previews.length === 0) return null;
  // One more than the batch is not hidden: it shows.
  const folded = previews.length > MESSAGE_PREVIEWS + 1 && !expanded;
  return (
    <div className="grid w-full gap-2" data-message-previews>
      {(folded ? previews.slice(0, MESSAGE_PREVIEWS) : previews).map((preview) => (
        <div className="contents" key={preview.key}>
          {preview.node}
        </div>
      ))}
      {previews.length > MESSAGE_PREVIEWS + 1 && (
        <SeeAllButton
          className="justify-self-start"
          count={previews.length}
          expanded={expanded}
          onToggle={() => setExpanded((value) => !value)}
        />
      )}
    </div>
  );
}
